import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, SidebarNavTrigger, useIsMobile } from '@aidkitorg/component-library';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { ConfigurationContext, PublicConfigurationContext } from '../Context';
import { AidKitLogo, MiniAidKitLogo } from '../Util';
import { useLocalizedStrings } from '../Localization';
import { ThreeColumnPage } from './ThreeColumnPage';
import { LanguageDropdown } from './LanguageDropdown';
import { PortalAccountMenu } from './PortalAccountMenu';
import { PortalHeader } from './PortalHeader';
import { get_deployment } from '../API';

// `/applicant/navigation` wraps entries in a single root node, so check for a real leaf rather than `nodes.length`.
function hasNavigableItems(nodes?: NavigationNode[]): boolean {
  return !!nodes && nodes.some(n =>
    (!n.isFolder && !!n.href && n.href !== '#') || hasNavigableItems(n.subnodes)
  );
}

/**
 * The Applicant Portal chrome: the top header (PortalHeader with hamburger nav +
 * account menu, or the legacy logo/language header) wrapping `main`, inside the
 * ThreeColumnPage nav layout. Extracted so both the portal pages and the
 * post-submission confirmation page render the same nested chrome.
 */
export function ApplicantPortalShell(props: {
  main: React.ReactNode,
  nav: NavigationNode[],
  Viewer: 'screener' | 'applicant',
  isLoggedIn: boolean,
  isDistroPreview?: boolean,
}) {
  const configuration = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const history = useHistory();
  const L = useLocalizedStrings();
  const isMobile = useIsMobile();

  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configuration.applicant_facing_logo;
  const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || configuration.applicant_facing_logo_width || '150';
  const programName = publicConfig.name || configuration.program_name || 'AidKit Program';

  const logo = applicantFacingLogo
    ? <img
      className="w-auto max-w-[150px] max-h-[125px]"
      alt={programName}
      src={applicantFacingLogo}
      width={applicantFacingLogoWidth}
    />
    : (
      <>
        <div className="hidden sm:block">
          <AidKitLogo width={100} height={40} />
        </div>
        <div className="block sm:hidden">
          <MiniAidKitLogo width={40} height={40} />
        </div>
      </>
    );

  const languageDropdown = <LanguageDropdown languages={(configuration.languages || 'en,es').split(',')} />;

  const hideLoginButton = get_deployment() === 'muwalicecredit' && Date.now() <= 1792432800000;

  const main = (
    <div className="max-w-4xl mx-auto applicant-led pb-36">
      {publicConfig.applicantPortal ? (
        <PortalHeader
          leftSlot={hasNavigableItems(props.nav)
            ? ((props.Viewer === 'applicant' || props.isDistroPreview) && <SidebarNavTrigger appearance="hamburger" />)
            : languageDropdown}
          rightSlot={(props.Viewer === 'applicant' || props.isDistroPreview) && (
            props.isLoggedIn
              ? <PortalAccountMenu />
              : (hideLoginButton ? null : <Button variant="ghost" onClick={() => history.push('/o/login')}>{L.auth.sign_up_log_in}</Button>)
          )}
        />
      ) : (
        <header className="bg-white sticky top-0">
          <div className="flex max-w-7xl mx-auto py-2 px-2 sm:px-6 lg:px-8 border-t-0 border-l-0 border-r-0 border-b border-solid border-gray-200 items-center justify-between gap-2 sm:gap-4">
            {isMobile && <SidebarNavTrigger className="!bg-gray-50 !text-gray-700 !font-normal" />}
            <div className="flex-shrink sm:flex-1 justify-self-start">{logo}</div>
            <div>{languageDropdown}</div>
          </div>
        </header>
      )}
      {props.main}
    </div>
  );

  return <ThreeColumnPage main={main} Viewer={props.Viewer} nav={props.nav} applicantPortal isDistroPreview={props.isDistroPreview} />;
}
